import React from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import { getCiReportRateIntegrity } from "../../../api/competitiveIntelligence";
import { PanelTakeaway, fmtDateShort, fmtRate } from "../shared";

// integrity_flag is "<KIND>: <message>". Color by KIND so warnings and
// opportunities are visually distinct.
const parseFlag = (raw) => {
  if (!raw) return { kind: "INFO", message: "" };
  const colonIndex = raw.indexOf(":");
  if (colonIndex === -1) return { kind: "INFO", message: raw.trim() };
  return { kind: raw.slice(0, colonIndex).trim().toUpperCase(), message: raw.slice(colonIndex + 1).trim() };
};

const KIND_STYLES = {
  WARNING: {
    border: "border-l-red-500",
    badge: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    icon: "text-red-500",
  },
  OPPORTUNITY: {
    border: "border-l-amber-500",
    badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    icon: "text-amber-500",
  },
  INFO: {
    border: "border-l-gray-400",
    badge: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    icon: "text-gray-400",
  },
};

const FlagIcon = ({ kind }) => {
  const cls = KIND_STYLES[kind]?.icon || KIND_STYLES.INFO.icon;
  if (kind === "OPPORTUNITY") {
    return (
      <svg className={`w-5 h-5 flex-shrink-0 ${cls}`} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M11 17a1 1 0 11-2 0 1 1 0 012 0zm0-9.5V13a1 1 0 11-2 0V7.5a1 1 0 112 0z" />
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
      </svg>
    );
  }
  return (
    <svg className={`w-5 h-5 flex-shrink-0 ${cls}`} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.345 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a1 1 0 011 1v4a1 1 0 11-2 0V6a1 1 0 011-1zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
    </svg>
  );
};

// Group rows by (kind, message, site_type) so 25 identical warnings collapse
// into one card with the affected dates listed. Keys: `${kind}|${message}|${siteType}`.
const groupRows = (rows) => {
  const groups = new Map();
  rows.forEach((row) => {
    const { kind, message } = parseFlag(row.integrity_flag);
    const key = `${kind}|${message}|${row.site_type || ""}`;
    if (!groups.has(key)) {
      groups.set(key, {
        kind,
        message,
        site_type: row.site_type,
        category_code: row.category_code,
        dates: [],
        prices: [],
      });
    }
    const group = groups.get(key);
    if (row.check_in) group.dates.push(row.check_in);
    if (row.price_per_night != null) group.prices.push(Number(row.price_per_night));
  });
  return [...groups.values()];
};

const RateIntegrityCard = ({ group }) => {
  const style = KIND_STYLES[group.kind] || KIND_STYLES.INFO;
  const minPrice = group.prices.length ? Math.min(...group.prices) : null;
  const maxPrice = group.prices.length ? Math.max(...group.prices) : null;
  const sortedDates = [...group.dates].sort();
  const previewDates = sortedDates.slice(0, 8);

  return (
    <div className={`rounded-lg border border-gray-200 dark:border-gray-800 border-l-[3px] ${style.border} bg-white dark:bg-gray-900 px-4 py-3.5`}>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3">
        <FlagIcon kind={group.kind} />
        {/* Left column: title, site type, price, date chips. Meta moved
            to the right column to balance the layout — same pattern as
            AiPricingActions and CompetitorProfiles cards. */}
        <div className="flex-1 min-w-0 pr-3">
          <p className="text-sm text-gray-900 dark:text-gray-100 font-semibold">{group.message}</p>
          {group.site_type && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate" title={group.site_type}>
              {group.site_type}
            </p>
          )}
          {minPrice != null && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Price: {fmtRate(minPrice)}{minPrice !== maxPrice ? ` – ${fmtRate(maxPrice)}` : ""}
            </p>
          )}
          {previewDates.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {previewDates.map((date) => (
                <span key={date} className="inline-block px-1.5 py-0.5 rounded text-[11px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  {fmtDateShort(date)}
                </span>
              ))}
              {sortedDates.length > previewDates.length && (
                <span className="inline-block px-1.5 py-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                  +{sortedDates.length - previewDates.length} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right column: kind badge + category on top, count below. */}
        <div className="flex-shrink-0 flex flex-col items-end gap-1 text-right">
          <div className="flex items-center gap-1.5">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold ${style.badge}`}>
              {group.kind}
            </span>
            {group.category_code && (
              <span className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                {group.category_code}
              </span>
            )}
          </div>
          <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {sortedDates.length} {sortedDates.length === 1 ? "date" : "dates"}
          </span>
        </div>
      </div>
    </div>
  );
};

const RateIntegrityBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No pricing hierarchy issues detected." />;
  }
  const groups = groupRows(rows);
  // WARNING first, then OPPORTUNITY, then INFO. Within a kind, more dates first.
  const KIND_RANK = { WARNING: 0, OPPORTUNITY: 1, INFO: 2 };
  const sorted = [...groups].sort((groupA, groupB) => {
    const rankDiff = (KIND_RANK[groupA.kind] ?? 9) - (KIND_RANK[groupB.kind] ?? 9);
    if (rankDiff !== 0) return rankDiff;
    return groupB.dates.length - groupA.dates.length;
  });

  return (
    <div className="p-5 space-y-3">
      {sorted.map((group, index) => (
        <RateIntegrityCard key={`${group.kind}-${group.message}-${group.site_type}-${index}`} group={group} />
      ))}
    </div>
  );
};

const RateIntegrityPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`rate-integrity-${clientId}-${categoryCode || "all"}`}
    title="Rate Integrity"
    tip="Flags pricing-hierarchy mistakes — e.g. a Deluxe site priced below a Standard site, or two tiers priced the same. Warnings are errors to fix; opportunities are small differentiation wins."
    description="Pricing hierarchy issues — site types priced inconsistently with their tier."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportRateIntegrity(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      // 8O wraps the array in `data.results` (other endpoints return `data` directly).
      // Fall back to data so it still works if the backend later normalizes.
      const rows = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data) ? data : [];
      return (
        <>
          <PanelTakeaway panelKey="rate_integrity" say={say} />
          <RateIntegrityBody rows={rows} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default RateIntegrityPanel;
